/**
 * Salon document multipart uploads: filename extension must match API allowlists.
 * Salon-wide (no clientId): pdf, png, jpg, jpeg, webp, gif — max 25 MB (server).
 * Client / PHI (clientId set): docx, pdf, jpg, jpeg, png, xlsx, xlsm, xlsb, xltx, xls.
 * Never use `.bin` / `.json` / `.txt` on the file part when the server infers from name.
 */

const SALON_WIDE_EXTENSIONS = new Set([
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
]);

const PHI_CLIENT_EXTENSIONS = new Set([
  "docx",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "xlsx",
  "xlsm",
  "xlsb",
  "xltx",
  "xls",
]);

const MIME_TO_EXTENSION_SALON_WIDE: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "application/pdf": ".pdf",
};

const MIME_TO_EXTENSION_PHI: Record<string, string> = {
  ...MIME_TO_EXTENSION_SALON_WIDE,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    ".docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.ms-excel.sheet.macroEnabled.12": ".xlsm",
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12": ".xlsb",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
    ".xltx",
  "application/vnd.ms-excel": ".xls",
};

function mimeTable(isClientScoped: boolean): Record<string, string> {
  return isClientScoped ? MIME_TO_EXTENSION_PHI : MIME_TO_EXTENSION_SALON_WIDE;
}

function extensionForMime(
  mime: string | undefined,
  isClientScoped: boolean,
): string | undefined {
  if (!mime) return undefined;
  const table = mimeTable(isClientScoped);
  const primary = mime.split(";")[0].trim().toLowerCase();
  if (table[primary]) return table[primary];
  if (primary.startsWith("image/")) {
    const allowed = isClientScoped
      ? PHI_CLIENT_EXTENSIONS
      : SALON_WIDE_EXTENSIONS;
    const sub = primary.slice("image/".length).split("+")[0]?.toLowerCase();
    if (sub === "jpeg" || sub === "jpg") return ".jpg";
    if (sub && allowed.has(sub)) return `.${sub}`;
  }
  return undefined;
}

/**
 * Filename for salon document multipart upload. Uses File.name when present;
 * otherwise `${documentKey}` + extension from contentType / Blob.type.
 */
export function salonDocumentUploadFilename(
  documentKey: string,
  file: File | Blob,
  contentType: string,
  isClientScoped = false,
): string {
  if (file instanceof File && file.name?.trim()) {
    return file.name;
  }
  const ext =
    extensionForMime(contentType, isClientScoped) ||
    (file instanceof Blob
      ? extensionForMime(file.type, isClientScoped)
      : undefined);
  if (ext) {
    return `${documentKey}${ext}`;
  }
  console.warn(
    "[uploadSalonDocument] Unmapped content_type; using .png for filename (extend MIME map if needed):",
    contentType,
  );
  return `${documentKey}.png`;
}
